import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Amplify } from 'aws-amplify';
import {
  confirmSignIn as amplifyConfirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import { Box, CircularProgress } from '@mui/material';
import { useConfig } from './ConfigProvider';

/**
 * What sign-in needs next, if anything.
 *
 * `newPassword` is the ordinary path for an invited user, not an edge case: `adminCreateUser` issues
 * a **temporary** password, so Cognito challenges the first sign-in and refuses to issue tokens until
 * a permanent one is set. Without handling it, every invited account is permanently locked out —
 * only the seeded dev admin, created with a permanent password, could ever get in.
 */
export type SignInStep = 'done' | 'newPassword';

interface AuthenticationContextType {
  email?: string;
  groups: string[];
  signIn: (email: string, password: string) => Promise<SignInStep>;
  /** Completes a `newPassword` challenge. Only valid immediately after `signIn` returned it. */
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string>;
}

// Defaults exist only so the context has a shape; the provider always replaces them.
export const AuthenticationContext = createContext<AuthenticationContextType>({
  groups: [],
  signIn: () => Promise.reject(new Error('AuthenticationProvider missing')),
  completeNewPassword: () =>
    Promise.reject(new Error('AuthenticationProvider missing')),
  signOut: () => Promise.resolve(),
  getIdToken: () => Promise.reject(new Error('AuthenticationProvider missing')),
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuthentication = () => useContext(AuthenticationContext);

/**
 * Cognito auth via Amplify, using **SRP with an in-app form** rather than the hosted-UI redirect that
 * CVDS uses.
 *
 * The reason is pragmatic: SRP works from any origin, so the PoC can be opened on the CloudFront
 * domain, a custom domain or localhost without maintaining a matching callback-URL list. It also keeps
 * the login screen inside the Universal+ theme instead of handing users a Cognito-branded page. The
 * user pool already enables `userSrp`, so this needs no infrastructure change.
 */
const AuthenticationProvider = ({ children }: { children: ReactNode }) => {
  const config = useConfig();
  const [email, setEmail] = useState<string>();
  const [groups, setGroups] = useState<string[]>([]);
  const [initialising, setInitialising] = useState(true);

  useEffect(() => {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.authUserPoolId,
          userPoolClientId: config.authUserPoolWebClientId,
        },
      },
    });

    // Restore an existing session on reload so a refresh does not bounce the operator to login.
    void (async () => {
      try {
        await getCurrentUser();
        await refreshIdentity();
      } catch {
        // No session — the router shows the login screen.
      } finally {
        setInitialising(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.authUserPoolId, config.authUserPoolWebClientId]);

  const refreshIdentity = useCallback(async () => {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;
    setEmail(typeof payload?.email === 'string' ? payload.email : undefined);
    setGroups((payload?.['cognito:groups'] as string[] | undefined) ?? []);
  }, []);

  const signIn = useCallback(
    async (username: string, password: string): Promise<SignInStep> => {
      const { isSignedIn, nextStep } = await amplifySignIn({
        username,
        password,
      });

      if (isSignedIn) {
        await refreshIdentity();
        return 'done';
      }

      if (
        nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        return 'newPassword';
      }

      // Anything else is TOTP, SMS or a hosted-UI redirect. None is configured on this user pool, so
      // reaching here means the pool changed — name the step rather than reporting it as generic
      // failure, because the step is the only useful thing to know.
      throw new Error(
        `This account requires "${nextStep.signInStep}", which the PoC does not implement.`,
      );
    },
    [refreshIdentity],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      const { isSignedIn } = await amplifyConfirmSignIn({
        challengeResponse: newPassword,
      });
      if (!isSignedIn) {
        throw new Error('Password was set but sign-in did not complete.');
      }
      await refreshIdentity();
    },
    [refreshIdentity],
  );

  const signOut = useCallback(async () => {
    await amplifySignOut();
    setEmail(undefined);
    setGroups([]);
  }, []);

  const getIdToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      throw new Error('Not signed in');
    }
    return token;
  }, []);

  const value = useMemo<AuthenticationContextType>(
    () => ({ email, groups, signIn, completeNewPassword, signOut, getIdToken }),
    [email, groups, signIn, completeNewPassword, signOut, getIdToken],
  );

  if (initialising) {
    return (
      <Box sx={{ display: 'flex' }}>
        <CircularProgress sx={{ mx: 'auto', mt: 4 }} />
      </Box>
    );
  }

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationProvider;
